import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  ChevronRight, 
  ChevronDown, 
  Users, 
  Folder,
  FileText
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useApiClient } from '../utils/authenticatedApiClient';
import { API_ENDPOINTS } from '../utils/apiClient';
import { AuthButton } from '../components/AuthButton';

interface Team {
  id: string;
  name: string;
  createdAt: Date;
  boardCount: number;
}

interface Board {
  id: string;
  title: string;
  templateId: string;
  teamId: string;
  createdAt: Date;
  updatedAt: Date;
  isArchived: boolean;
}

const TeamsPage: React.FC = () => {
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamBoards, setTeamBoards] = useState<{ [teamId: string]: Board[] }>({});
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await apiClient.get<Team[]>(API_ENDPOINTS.TEAMS);
      if (response.data) {
        setTeams(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch teams');
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamBoards = async (teamId: string) => {
    try {
      const response = await apiClient.get<Board[]>(`/api/teams/${teamId}/boards`);
      if (response.data) {
        setTeamBoards(prev => ({ ...prev, [teamId]: response.data! }));
      } else {
        throw new Error(response.error || 'Failed to fetch team boards');
      }
    } catch (error) {
      console.error('Error fetching team boards:', error);
      toast.error('Failed to load team boards');
    }
  };

  const handleTeamClick = (teamId: string) => {
    if (expandedTeams.has(teamId)) {
      setExpandedTeams(prev => {
        const newSet = new Set(prev);
        newSet.delete(teamId);
        return newSet;
      });
    } else {
      setExpandedTeams(prev => new Set(prev).add(teamId));
      if (!teamBoards[teamId]) {
        fetchTeamBoards(teamId);
      }
    }
  };

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;

    try {
      const response = await apiClient.post<Team>(API_ENDPOINTS.TEAMS, {
        name: newTeamName.trim()
      });

      if (response.data) {
        setTeams(prev => [...prev, response.data!]);
        setNewTeamName('');
        setShowCreateTeam(false);
        toast.success('Team created successfully');
      } else {
        throw new Error(response.error || 'Failed to create team');
      }
    } catch (error) {
      console.error('Error creating team:', error);
      toast.error('Failed to create team');
    }
  };

  const handleBoardClick = (boardId: string) => {
    navigate(`/board/${boardId}`);
  };

  const handleCreateNewBoard = (teamId: string, teamName: string) => {
    navigate(`/templates?team=${encodeURIComponent(teamName)}&teamId=${teamId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading teams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link
              to="/"
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-white/50 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Home
            </Link>
            
            <div className="flex items-center">
              <Users className="h-8 w-8 text-indigo-600 mr-3" />
              <h1 className="text-3xl font-bold text-gray-900">Retro Teams</h1>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <AuthButton />
            <button
              onClick={() => setShowCreateTeam(true)}
              className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Team
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Left Sidebar - Teams List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Teams</h2>
              
              {teams.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Folder className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-sm mb-4">No teams yet</p>
                  <button
                    onClick={() => setShowCreateTeam(true)}
                    className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                  >
                    Create your first team
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {teams.map((team) => (
                    <div key={team.id} className="border rounded-lg">
                      <button
                        onClick={() => handleTeamClick(team.id)}
                        className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center">
                          <Folder className="h-5 w-5 text-blue-500 mr-3" />
                          <div>
                            <h3 className="font-medium text-gray-900">{team.name}</h3>
                            <p className="text-sm text-gray-500">{team.boardCount} boards</p>
                          </div>
                        </div>
                        {expandedTeams.has(team.id) ? (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                      
                      {expandedTeams.has(team.id) && (
                        <div className="border-t bg-gray-50 p-3">
                          {teamBoards[team.id] ? (
                            <div className="space-y-2">
                              {teamBoards[team.id].map((board) => (
                                <button
                                  key={board.id}
                                  onClick={() => handleBoardClick(board.id)}
                                  className="w-full flex items-center p-2 text-left hover:bg-white rounded transition-colors"
                                >
                                  <FileText className="h-4 w-4 text-green-500 mr-2" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {board.title}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {new Date(board.updatedAt).toLocaleDateString()}
                                    </p>
                                  </div>
                                </button>
                              ))}
                              
                              <button
                                onClick={() => handleCreateNewBoard(team.id, team.name)}
                                className="w-full flex items-center p-2 text-left hover:bg-white rounded transition-colors border-2 border-dashed border-gray-300 hover:border-indigo-300"
                              >
                                <Plus className="h-4 w-4 text-indigo-500 mr-2" />
                                <span className="text-sm text-indigo-600 font-medium">
                                  Create New Board
                                </span>
                              </button>
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Content Area */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center py-16">
                <Users className="h-24 w-24 text-gray-300 mx-auto mb-6" />
                <h3 className="text-xl font-semibold text-gray-600 mb-4">
                  Select a team to view boards
                </h3>
                <p className="text-gray-500 mb-8">
                  Choose a team from the sidebar to see all retrospective boards, or create a new team to get started.
                </p>
                
                {teams.length === 0 && (
                  <button
                    onClick={() => setShowCreateTeam(true)}
                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Create Your First Team
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Create Team Modal */}
        {showCreateTeam && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Create New Team</h3>
              
              <form onSubmit={handleCreateTeam}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Name
                  </label>
                  <input
                    type="text"
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    placeholder="Enter team name..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    autoFocus
                    required
                  />
                </div>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateTeam(false);
                      setNewTeamName('');
                    }}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                  >
                    Create Team
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamsPage;